'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { trackServiceInquiry } from '@/lib/analytics';

interface ContactMethod {
  id: string;
  icon: string;
  title: string;
  value: string;
  description: string;
  action: string;
}

const DirectContactClient = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const contactMethods: ContactMethod[] = [
    {
      id: 'phone',
      icon: 'PhoneIcon',
      title: 'Call Us',
      value: '+243981944976',
      description: '24/7 Emergency Hotline',
      action: 'tel:+243981944976'
    },
    {
      id: 'email',
      icon: 'EnvelopeIcon',
      title: 'Email Us',
      value: 'mylesdrivelc@gmail.com',
      description: 'Response within 2 hours',
      action: 'mailto:mylesdrivelc@gmail.com'
    },
    {
      id: 'whatsapp',
      icon: 'ChatBubbleLeftRightIcon',
      title: 'WhatsApp',
      value: '+243981944976',
      description: 'Instant messaging support',
      action: 'https://wa.me/243981944976'
    },
    {
      id: 'emergency',
      icon: 'ExclamationTriangleIcon',
      title: 'Emergency Line',
      value: '+243981944976',
      description: 'Critical shipment issues',
      action: 'tel:+243981944976'
    }
  ];

  if (!isHydrated) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-pulse">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-muted rounded-lg h-40"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {contactMethods.map((method) => (
        <a
          key={method.id}
          href={method.action}
          target={method.id === 'whatsapp' ? '_blank' : undefined}
          rel={method.id === 'whatsapp' ? 'noopener noreferrer' : undefined}
          onMouseEnter={() => setSelectedMethod(method.id)}
          onMouseLeave={() => setSelectedMethod(null)}
          onClick={() => {
            // Track service inquiry conversion
            trackServiceInquiry(method.title, method.id);
          }}
          className={`group bg-card rounded-xl p-6 border-2 transition-all duration-300 ${
            selectedMethod === method.id
              ? 'border-golden-mid shadow-golden-md -translate-y-1'
              : 'border-border hover:border-golden-light hover:shadow-elevation-2'
          }`}
        >
          <div className="flex items-start space-x-4">
            <div className={`flex-shrink-0 w-14 h-14 rounded-lg flex items-center justify-center transition-all duration-300 ${
              selectedMethod === method.id
                ? 'bg-gradient-to-br from-golden-light to-golden-dark' :'bg-muted group-hover:bg-gradient-to-br group-hover:from-golden-light/20 group-hover:to-golden-dark/20'
            }`}>
              <Icon 
                name={method.icon as any} 
                size={28} 
                className={selectedMethod === method.id ? 'text-primary' : 'text-muted-foreground group-hover:text-golden-mid'}
              />
            </div>
            <div className="flex-1">
              <h3 className="font-headline font-bold text-lg text-foreground mb-1">{method.title}</h3>
              <p className="font-mono text-primary font-semibold mb-2">{method.value}</p>
              <p className="font-body text-sm text-muted-foreground">{method.description}</p>
            </div>
            <Icon 
              name="ArrowTopRightOnSquareIcon" 
              size={20} 
              className={`flex-shrink-0 transition-all duration-300 ${
                selectedMethod === method.id ? 'text-golden-mid translate-x-1 -translate-y-1' : 'text-muted-foreground'
              }`}
            />
          </div>
        </a>
      ))}
    </div>
  );
};

export default DirectContactClient;